import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Building2,
  Check,
  X,
  ArrowLeft,
  Zap,
  Crown,
  HelpCircle,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const PLANS = [
  {
    id: 'base',
    name: 'Base',
    price: 19,
    description: 'Perfect for solo contractors and small operations',
    icon: Building2,
    color: 'bg-blue-600',
    features: [
      { name: 'Unlimited leads', included: true },
      { name: 'Unlimited projects', included: true },
      { name: 'Client portal', included: true },
      { name: 'Document storage (5GB)', included: true },
      { name: 'Email reminders', included: true },
      { name: 'CSV lead import', included: true },
      { name: 'Team members', included: false, note: '+$9/manager' },
      { name: 'Priority support', included: false },
      { name: 'Advanced reporting', included: false },
      { name: 'White-label portal', included: false },
      { name: 'API access', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    description: 'For growing teams ready to scale',
    icon: Zap,
    color: 'bg-orange-500',
    popular: true,
    features: [
      { name: 'Unlimited leads', included: true },
      { name: 'Unlimited projects', included: true },
      { name: 'Client portal', included: true },
      { name: 'Document storage (25GB)', included: true },
      { name: 'Email reminders', included: true },
      { name: 'CSV lead import', included: true },
      { name: '2 Project Managers included', included: true },
      { name: '2 Forepersons included', included: true },
      { name: 'Priority support', included: true },
      { name: 'Advanced reporting', included: true },
      { name: 'White-label portal', included: false },
      { name: 'API access', included: false },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: 79,
    description: 'For established companies with large teams',
    icon: Crown,
    color: 'bg-purple-600',
    features: [
      { name: 'Unlimited leads', included: true },
      { name: 'Unlimited projects', included: true },
      { name: 'Client portal', included: true },
      { name: 'Document storage (100GB)', included: true },
      { name: 'Email reminders', included: true },
      { name: 'CSV lead import', included: true },
      { name: 'Unlimited team members', included: true },
      { name: 'Priority support', included: true },
      { name: 'Advanced reporting', included: true },
      { name: 'White-label portal', included: true },
      { name: 'API access', included: true },
      { name: 'Dedicated account manager', included: true },
    ],
  },
];

const FAQS = [
  {
    question: 'How does the 30-day free trial work?',
    answer: 'You get full access to all features for 30 days, no credit card required. At the end of your trial, you can choose a plan that fits your needs. Your data is saved and ready to go.',
  },
  {
    question: 'Can I change my plan later?',
    answer: 'Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any charges.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, MasterCard, American Express, Discover) through our secure payment processor, Stripe.',
  },
  {
    question: 'Is there a contract or commitment?',
    answer: 'No contracts! All plans are month-to-month. You can cancel anytime and your access continues until the end of your billing period.',
  },
  {
    question: 'How do I add team members?',
    answer: 'On the Base plan, you can add project managers at $9/month each. Pro includes 2 managers and 2 forepersons. Business has unlimited team members included.',
  },
  {
    question: 'Is my data secure?',
    answer: 'Yes! We use industry-standard encryption (SSL/TLS) for all data in transit, and your data is stored securely with regular backups. We never sell your information.',
  },
  {
    question: 'Do you offer discounts for annual billing?',
    answer: 'Contact our sales team for annual billing options and volume discounts for larger teams.',
  },
];

export default function Pricing() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 bg-orange-500 rounded-sm flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg">Corbel</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="sm" className="rounded-sm">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Start with a 30-day free trial. No credit card required. 
            Choose the plan that fits your business when you're ready.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {PLANS.map((plan) => (
              <Card 
                key={plan.id} 
                className={`rounded-sm relative ${plan.popular ? 'border-2 border-orange-500 shadow-lg' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-orange-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className={`h-12 w-12 ${plan.color} rounded-sm flex items-center justify-center text-white mb-4`}>
                    <plan.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <p className="text-slate-500 text-sm">{plan.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">${plan.price}</span>
                    <span className="text-slate-500">/month</span>
                  </div>

                  <Link to="/register">
                    <Button 
                      className={`w-full rounded-sm mb-6 ${plan.popular ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      Start Free Trial
                    </Button>
                  </Link>

                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                        ) : (
                          <X className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" />
                        )}
                        <span className={feature.included ? 'text-slate-700' : 'text-slate-400'}>
                          {feature.name}
                          {feature.note && <span className="text-orange-500 text-sm ml-1">({feature.note})</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-8">
            Compare Plans
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Base</th>
                  <th className="text-center py-4 px-4 font-semibold text-orange-500">Pro</th>
                  <th className="text-center py-4 px-4 font-semibold text-slate-900">Business</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Leads & Projects', base: 'Unlimited', pro: 'Unlimited', business: 'Unlimited' },
                  { feature: 'Document Storage', base: '5 GB', pro: '25 GB', business: '100 GB' },
                  { feature: 'Client Portal', base: true, pro: true, business: true },
                  { feature: 'Email Reminders', base: true, pro: true, business: true },
                  { feature: 'Project Managers', base: '+$9/each', pro: '2 included', business: 'Unlimited' },
                  { feature: 'Forepersons', base: false, pro: '2 included', business: 'Unlimited' },
                  { feature: 'Google Calendar Sync', base: true, pro: true, business: true },
                  { feature: 'Priority Support', base: false, pro: true, business: true },
                  { feature: 'Advanced Reporting', base: false, pro: true, business: true },
                  { feature: 'White-label Portal', base: false, pro: false, business: true },
                  { feature: 'API Access', base: false, pro: false, business: true },
                ].map((row, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-4 px-4 text-slate-700">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.base === 'boolean' ? (
                        row.base ? <Check className="h-5 w-5 text-green-500 mx-auto" /> : <X className="h-5 w-5 text-slate-300 mx-auto" />
                      ) : (
                        <span className="text-slate-600">{row.base}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center bg-orange-50">
                      {typeof row.pro === 'boolean' ? (
                        row.pro ? <Check className="h-5 w-5 text-green-500 mx-auto" /> : <X className="h-5 w-5 text-slate-300 mx-auto" />
                      ) : (
                        <span className="text-slate-600">{row.pro}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.business === 'boolean' ? (
                        row.business ? <Check className="h-5 w-5 text-green-500 mx-auto" /> : <X className="h-5 w-5 text-slate-300 mx-auto" />
                      ) : (
                        <span className="text-slate-600">{row.business}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <HelpCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-sm border px-6">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to get started?
          </h2>
          <p className="text-slate-300 mb-8">
            Start your free 30-day trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="rounded-sm bg-orange-500 hover:bg-orange-600">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="rounded-sm border-slate-600 text-white hover:bg-slate-800">
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-orange-500 rounded-sm flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-white">Corbel</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white">Terms of Service</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
